<template>
    <b-container fluid>
        <h1 class="text-left">{{title}}</h1>
        <br/>
        <b-form ref="cooperationForm" class="col-xs-12 col-lg-9 pb-5" name="cooperationForm" v-on:submit.prevent="_onSubmit">
            <!-- Cooperation name -->
            <x-input :text="labelName"
                     v-model="record.name"
                     @input="_onInputName"
                     :id="'cooperation-name'"
                     :name="'cooperation-name'"
                     :invalidMessage="nameMessage"
                     :state="!errorInUse"
                     :placeholder="placeholders.name"
                     :maxlength="50"
                     :required="true"
                     :autofocus="true"
                     :readonly="record.readonly">
            </x-input>
            <br/>
            <dual-listbox
                :titleAvailable="$t('cooperation.labelAvailableOrganizations')"
                :titleSelected="$t('cooperation.labelSelectedOrganizations')"
                :itemsAvailable="availableOrganizations"
                :itemsSelected="selectedOrganizations"
                :items="allOrganizations"
                :itemDisplayName="'name'"
                :showUpDownButtons="false"
                @update="updateOrganizations($event)"
            >
            </dual-listbox>
            <!-- add / cancel buttons -->
            <submit-cancel-remove :is-saving="isSaving"
                                  :is-deleting="isDeleting"
                                  :mode="mode"
                                  @cancel="_onCancel"
                                  @remove="_onRemove"
                                  :is-btn-save-disabled="isBtnSaveDisabled"
                                  :is-btn-remove-disabled="isBtnRemoveDisabled">
            </submit-cancel-remove>
        </b-form>
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT } from '../../constants';
    import DualListBox from '../../components/DualListBox.vue';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import { debounce } from 'lodash';

    export default {
        name: 'CooperationDetail',

        mixins: [
            modalMixin,
        ],

        components: {
            'dual-listbox': DualListBox,
            'x-input': XInput,
            'submit-cancel-remove': SubmitCancelRemove,
        },

        props: { id: { type: Number, required: false, default: -1 } },

        data: function () {
            return {
                // Cooperation record
                record: {},
                // organizations list
                organizations: [],
                // labels for input fields
                labelName: this.$t('columnNameName'),
                // placeholders list
                placeholders: {
                    name: this.$t('cooperation.name'),
                },
                // state variables
                isSaving: false,
                isDeleting: false,
                // add/edit mode
                mode: 0,
                // page title
                title: '',
                // validation
                errorInUse: false,
                nameMessage: ''
            };
        },
        computed: {

            availableOrganizations() {
                return this.organizations.filter(o => !o.userEnabled);
            },

            selectedOrganizations() {
                return this.organizations.filter(o => o.userEnabled);
            },

            allOrganizations() {
                return this.organizations;
            },

            /**
              * Check if OWS can be saved
              * @returns {boolean} indicating if OWS can be saved
              */
            isBtnSaveDisabled() {
                return this.errorInUse
                    || this.isSaving
                    || this.isDeleting;
            },

            isBtnRemoveDisabled() {
                return this.record.readonly;
            },

        },

        async mounted() {
            if (this.id && this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('cooperation.detailEditTitle');
                // get existing ows data from the backend
                await this._loadExistingRecord();
            } else {
                // new form
                this.mode = MODE_ADD;
                this.title = this.$t('cooperation.detailAddTitle');
            }

            await this._loadOrganizations();
        },

        methods: {

            async _loadExistingRecord() {
                nprogress.start();
                // get existing cooperation data
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetCooperation',
                    parameters: {
                        id: this.id
                    }
                });
                this.record = { ...result.data };
                nprogress.done();
            },

            async _loadOrganizations() {
                // get all organizations
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetAllOrganizations',
                    parameters: {
                        id: this.id
                    }
                });
                // initialize userEnabled
                this.organizations = result.data.map(o => {
                    o.userEnabled = o.cooperationId && o.cooperationId > 0;
                    return o;
                });
                // filter organizations from other cooperations away
                if (this.mode === MODE_ADD) {
                    // only organizations not other cooperations
                    this.organizations = this.organizations.filter(o => !o.userEnabled);
                } else {
                    // organizations not in other cooperations +
                    // organizations in this cooperation
                    this.organizations = this.organizations.filter(o => !o.userEnabled || o.cooperationId === this.record.id);
                }
            },

            updateOrganizations(event) {
                this.organizations = event;
            },

            /**
             * check coo existence
             */
            _onInputName: debounce(
                async function (searchTerm) {
                    await this._checkCooperationName(searchTerm);
                },
                750
            ),

            async _checkCooperationName(searchTerm) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetCooperationExists',
                    parameters: {
                        cooperationId: this.id,
                        name: searchTerm
                    }
                });

                this.errorInUse = result.data;
                if (this.errorInUse) {
                    this.nameMessage = this.$t('cooperation.inUse');
                }
            },

            _gotoOverview() {
                this.$router.push({ path: '/dashboard/cooperations' });
            },

            async _onSubmit() {
                if (!this.$refs.cooperationForm.checkValidity()) {
                    return;
                }

                await this._checkCooperationName(this.record.name);

                if (this.errorInUse) {
                    return;
                }

                const selectedOrganizations = this.organizations.filter(o => o.userEnabled);
                // check length
                if (selectedOrganizations.length < 2) {
                    const message = this.$t('cooperation.selectedOrganizationsError');
                    this.showToast({
                        title: this.$t('cooperation.unableToSave'),
                        message: message,
                        variant: 'info',
                        autoHideDelay: 5000,
                    });
                    return;
                }

                this.record.organizations = selectedOrganizations.map(o => {
                    return {
                        id: o.id,
                        name: o.name,
                        cooperationId: this.record.id
                    };
                });

                await this._onSave();
            },

            async _onSave() {
                this.isSaving = true;
                nprogress.start();

                try {
                    if (this.mode === MODE_ADD) {
                        await this.$store.dispatch('useApiData', {
                            name: 'CreateCooperation',
                            data: this.record
                        });
                        this._gotoOverview();
                    } else {
                        await this.$store.dispatch('useApiData', {
                            name: 'UpdateCooperation',
                            data: this.record
                        });
                        await this._loadExistingRecord();
                    }
                } catch (error) {
                    nprogress.done();
                    this.isSaving = false;
                    const message = this.$t(error.message, error.parameters);
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: message,
                        variant: 'danger',
                        autoHideDelay: 10000,
                    });
                } finally {
                    nprogress.done();
                    this.isSaving = false;
                }
            },

            _onCancel() {
                this._gotoOverview();
            },

            _onRemove() {
                const data = { message: this.$t('cooperation.deletionConfirmation') };
                this.msgBoxConfirm(data).then(async (confirmed) => {
                    if (confirmed) {
                        try {
                            const result = await this.$store.dispatch('useApiData', {
                                name: 'DeleteCooperation',
                                parameters: {
                                    id: this.id
                                }
                            });

                            if (result.data === this.id) {
                                this._gotoOverview();
                            }

                        } catch (error) {
                            this.showToast({
                                title: this.$t('deleteFailureTitle'),
                                message: this.$t('deleteFailureMessage', [`${error.response.data.message}`]),
                                variant: 'danger',
                                autoHideDelay: 10000,
                            });
                        }
                    }
                });
            }
        }
    };
</script>

<style scoped>
    .error {
        color: red;
    }
</style>
